import { z } from 'zod';

import { firstLastNameRegEx, phoneRegEx } from '@libs/common-next';
import { addressFormSchemaUSandCA } from '../AddressForm';
import {
    firstNameRequiredMsg,
    firstNameInvalidMsg,
    firstNameMaxCharactersMsg,
    lastNameInvalidMsg,
    lastNameRequiredMsg,
    lastNameMaxCharactersMsg,
    phoneRequiredMsg,
    phoneInvalidMsg,
    nameOnCardRequiredMsg,
    expirationDateRequiredMsg,
} from './validationErrors';
import { validateExpirationDate } from './utils';

const firstNameSchema = z
    .string()
    .trim()
    .min(1, firstNameRequiredMsg)
    .max(70, firstNameMaxCharactersMsg)
    .regex(firstLastNameRegEx, firstNameInvalidMsg);

const lastNameSchema = z
    .string()
    .trim()
    .min(1, lastNameRequiredMsg)
    .max(70, lastNameMaxCharactersMsg)
    .regex(firstLastNameRegEx, lastNameInvalidMsg);

const phoneSchema = z.string().min(1, phoneRequiredMsg).regex(phoneRegEx, phoneInvalidMsg);

const nameOnCardSchema = z.string().trim().min(1, nameOnCardRequiredMsg).max(30);

const expirationDateSchema = z
    .string()
    .trim()
    .min(1, expirationDateRequiredMsg)
    .superRefine((val, ctx) => {
        validateExpirationDate(val, ctx);
    });

export const addressTypeEnum = z.enum(['id', 'form']);

const paymentFormWithOptionalAddressSchema = z.object({
    addressType: z.literal(addressTypeEnum.enum.id),
    selectedAddressId: z.string(),
    firstName: z.optional(firstNameSchema),
    lastName: z.optional(lastNameSchema),
    phone: z.optional(phoneSchema),
    isPrimary: z.boolean(),
    address: addressFormSchemaUSandCA.optional(),
});

const paymentFormWithRequiredAddressSchema = z.object({
    addressType: z.literal(addressTypeEnum.enum.form),
    firstName: firstNameSchema,
    lastName: lastNameSchema,
    phone: phoneSchema,
    address: addressFormSchemaUSandCA,
    isPrimary: z.boolean(),
    selectedAddressId: z.optional(z.string()),
});

export const paymentFormSchema = z.discriminatedUnion('addressType', [
    paymentFormWithOptionalAddressSchema,
    paymentFormWithRequiredAddressSchema,
]);

export const billingAddressInputsSchema = z.object({
    firstName: firstNameSchema,
    lastName: lastNameSchema,
    phone: phoneSchema,
    address: addressFormSchemaUSandCA,
    selectedAddressId: z.optional(z.string()),
});

export const selectedAddressSchema = z.object({
    selectedAddressId: z.string(),
    address: z.optional(addressFormSchemaUSandCA),
});

export const cardInputsSchema = z.object({
    nameOnCard: nameOnCardSchema,
    expirationDate: expirationDateSchema,
    isPrimary: z.boolean(),
    cardId: z.number(),
    ccToken: z.string(),
});

export const editPaymentWithAddressObjectSchema = cardInputsSchema
    .merge(billingAddressInputsSchema)
    .extend({ addressType: z.literal(addressTypeEnum.enum.form) });

export const editPaymentWithAddressIdSchema = cardInputsSchema
    .merge(selectedAddressSchema)
    .extend({ addressType: z.literal(addressTypeEnum.enum.id) });

export const editPaymentFormSchema = z.discriminatedUnion('addressType', [
    editPaymentWithAddressObjectSchema,
    editPaymentWithAddressIdSchema,
]);

export type PaymentFormWithAddressObject = z.infer<typeof paymentFormWithRequiredAddressSchema>;
export type PaymentFormWithAddressId = z.infer<typeof paymentFormWithOptionalAddressSchema>;

export type AddPaymentFormInterface = z.infer<typeof paymentFormSchema>;

export type EditPaymentFormInterface = z.infer<typeof editPaymentFormSchema>;
